import React, { Component } from "react";
import styled from "styled-components";
import { Checkbox, FormControlLabel } from "@material-ui/core";

import { ShowDialog, ShowErrorDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import {
    WorkspaceDialog as StyledBeautifulDialog,
    WorkspaceDialogActionButton,
    WorkspaceDialogActions as StyledDialogActions,
    WorkspaceDialogBody as DialogBody,
    WorkspaceDialogContent as StyledDialogContent,
    WorkspaceDialogField as FieldGroup,
    WorkspaceDialogFieldLabel as FieldLabel,
    WorkspaceDialogTextField as StyledTextField,
    WorkspaceDialogTitle as StyledDialogTitle
} from "js/react/components/Dialogs/WorkspaceDialogStyles";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";
import WorkspaceController from "js/controllers/WorkspaceController";
import {
    ActionPermissionsObject,
    WorkspacePermissionSubjectType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserGroupType,
    WorkspaceUserRole
} from "common/interfaces";
import Banner from "js/Components/Banner";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { InviteTagsInput } from "js/react/components/InviteInput/InviteTagsInput";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import getLogger from "js/core/logger";

const logger = getLogger();
const FOLDER_NAME_MAX_LENGTH = 50;

const PermissionsCheckbox = styled(FormControlLabel)`
    &&& {
        .MuiFormControlLabel-label {
            color: #666;
            /* popup/fine print */
            font-family: "Source Sans Pro";
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 130%;
        }
    }
`;

const CreateButton = styled(WorkspaceDialogActionButton)`
    &&& {
        text-transform: uppercase;
    }
`;

const ShareUpgradeBanner = styled(Banner)`
    &&& {
        background: #F7FAFC;
        border-color: ${themeColors.lightBlue};
        padding: 10px 15px;

        .bai-button {
            background: #FFFFFF;
            color: #333333;
        }

        .bai-button span {
            color: #333333;
        }
    }
`;

function isTopLevelPresentationFolderNameTaken(name, folderType) {
    return WorkspaceController.resourceFolders
        .filter(f =>
            f.resourceTypes.includes(WorkspaceResourceType.PRESENTATION) &&
            f.folderType === folderType &&
            !f.parentFolderId
        )
        .some(f => f.name === name);
}

function buildFolderPermissionsFromSelection(selection = []) {
    const permissions = [];

    selection.forEach(item => {
        if (item.inviteError) {
            return;
        }

        if (item.type === "folder") {
            const folder = WorkspaceController.resourceFolders.find(f => f.id === item.id);
            if (folder?.userGroupId) {
                permissions.push({
                    subjectId: folder.userGroupId,
                    subjectType: WorkspacePermissionSubjectType.USER_GROUP
                });
            }
            return;
        }

        if (item.type === "group") {
            permissions.push({
                subjectId: item.id,
                subjectType: WorkspacePermissionSubjectType.USER_GROUP
            });
            return;
        }

        const uid = item.uid || item.id;
        if (!uid) {
            return;
        }

        const user = WorkspaceController.users.find(u => u.uid === uid);
        if (user?.role === WorkspaceUserRole.GUEST) {
            return;
        }

        permissions.push({
            subjectId: uid,
            subjectType: WorkspacePermissionSubjectType.USER
        });
    });

    return permissions;
}

export default class CreateFolderDialog extends Component {
    constructor(props) {
        super(props);

        const isCreatingShared = this.isCreatingSharedFolder();
        const inviteContext = isCreatingShared ? createInviteSuggestionContext({
            includeFolders: false,
            includeGroups: true,
            includeGroupTypes: [WorkspaceUserGroupType.GUESTS, WorkspaceUserGroupType.CUSTOM],
            prohibitExternalWorkspaceCollaboration: true,
            allowEmailInvites: false,
            saveContext: context => this.setState({ inviteContext: context }),
        }) : null;

        this.state = {
            folderName: "",
            allowMembersToManagePermissions: true,
            inviteContext,
            isCreating: false,
        };
    }

    componentWillUnmount() {
        this.state.inviteContext?.destroy();
    }

    canCreateSharedFolders = () => {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCE_FOLDERS].create &&
            WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    }

    getRequestedFolderType = () => {
        return this.props.folderType ?? WorkspaceResourceFolderType.SHARED;
    }

    isCreatingSharedFolder = () => {
        return this.getRequestedFolderType() === WorkspaceResourceFolderType.SHARED &&
            this.canCreateSharedFolders();
    }

    handleChange = evt => {
        this.setState({ folderName: evt.target.value });
    }

    handleToggleAllowMembersToManagePermissions = () => {
        this.setState(state => ({
            allowMembersToManagePermissions: !state.allowMembersToManagePermissions
        }));
    }

    handleAccept = async () => {
        const { acceptCallback, closeDialog } = this.props;
        const { folderName, inviteContext, allowMembersToManagePermissions, isCreating } = this.state;
        const trimmedFolderName = folderName.trim();
        if (!trimmedFolderName || isCreating) {
            return;
        }

        const folderType = this.getRequestedFolderType();
        const isCreatingShared = this.isCreatingSharedFolder();

        if (isTopLevelPresentationFolderNameTaken(trimmedFolderName, folderType)) {
            ShowWarningDialog({
                title: t("Unable to create folder"),
                message: t("Please enter a unique folder name.")
            });
            return;
        }

        const permissions = isCreatingShared
            ? buildFolderPermissionsFromSelection(inviteContext?.selection)
            : [];

        this.setState({ isCreating: true });
        try {
            const folder = await withProgressDialog({
                title: isCreatingShared ? t("Creating team folder...") : t("Creating folder..."),
                action: async () => {
                    const createdFolder = await WorkspaceController.createWorkspaceResourceFolder({
                        name: trimmedFolderName,
                        resourceTypes: [WorkspaceResourceType.PRESENTATION],
                        folderType,
                        ...(permissions.length ? { permissions } : {})
                    });

                    // Defaults already allow members to manage permissions; only override when unchecked.
                    if (isCreatingShared && allowMembersToManagePermissions === false) {
                        await WorkspaceController.updateWorkspaceResourceFolder({
                            folderId: createdFolder.id,
                            permissionsOverrides: {
                                [WorkspaceUserRole.LIBRARIAN]: {
                                    read: { object: true, permissions: true },
                                    write: { object: true, permissions: false },
                                    owner: false
                                },
                                [WorkspaceUserRole.MEMBER]: {
                                    read: { object: true, permissions: true },
                                    write: { object: true, permissions: false },
                                    owner: false
                                }
                            }
                        });
                    }

                    return createdFolder;
                }
            });

            acceptCallback?.(folder);
            closeDialog();
        } catch (err) {
            logger.error(err, "[CreateFolderDialog] Unable to create folder");
            ShowErrorDialog({
                title: t("Unable to create folder"),
                message: err.message
            });
            this.setState({ isCreating: false });
        }
    }

    handleCancel = () => {
        const { cancelCallback, closeDialog } = this.props;
        cancelCallback?.();
        closeDialog();
    }

    handleKeyPress = event => {
        if (event.key === "Enter" && !this.isCreatingSharedFolder()) {
            this.handleAccept();
        }
    }

    render() {
        const { folderName, inviteContext, allowMembersToManagePermissions, isCreating } = this.state;
        const okButtonEnabled = folderName.trim().length > 0 && !isCreating;
        const canCreateShared = this.canCreateSharedFolders();
        const isCreatingShared = this.isCreatingSharedFolder();

        return (
            <StyledBeautifulDialog closeDialog={this.props.closeDialog} maxWidth="md">
                <DialogBody>
                    <StyledDialogTitle>
                        {t("Create New Folder")}
                    </StyledDialogTitle>
                    <StyledDialogContent>
                        <FieldGroup>
                            <FieldLabel>{t("Folder Name")}</FieldLabel>
                            <StyledTextField
                                id="create_folder_input"
                                placeholder={t("Folder Name")}
                                variant="standard"
                                autoFocus={true}
                                value={folderName}
                                fullWidth={true}
                                type="text"
                                inputProps={{ maxLength: FOLDER_NAME_MAX_LENGTH }}
                                onChange={this.handleChange}
                                onKeyPress={this.handleKeyPress}
                            />
                        </FieldGroup>
                        {isCreatingShared && inviteContext && (
                            <>
                                <FieldGroup>
                                    <FieldLabel>{t("Add Members")}</FieldLabel>
                                    <InviteTagsInput
                                        inviteContext={inviteContext}
                                        sourceName="CreateFolderDialog"
                                    />
                                </FieldGroup>
                                <PermissionsCheckbox
                                    control={
                                        <Checkbox
                                            color="primary"
                                            size="small"
                                            checked={allowMembersToManagePermissions}
                                            onChange={this.handleToggleAllowMembersToManagePermissions}
                                        />
                                    }
                                    label={t("Allow members to manage folder permissions")}
                                />
                            </>
                        )}
                    </StyledDialogContent>
                    {!canCreateShared && <ShareUpgradeBanner
                        headerText={t("Unlock shared Team Folders")}
                        bodyText={t("Upgrade to our Team plan to collaborate seamlessly and keep your team’s presentations organized and easy to find.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog, {
                            featuredPlan: WorkspacePlanTier.TEAM,
                            analytics: { cta: "unlockSharedTeamFolders" }
                        })}
                    />}
                </DialogBody>
                <StyledDialogActions>
                    <WorkspaceDialogActionButton
                        unframed
                        large
                        color={themeColors.mediumGray}
                        onClick={this.handleCancel}
                        disabled={isCreating}
                    >
                        {t("Cancel")}
                    </WorkspaceDialogActionButton>
                    <CreateButton
                        large
                        blue
                        disabled={!okButtonEnabled}
                        onClick={this.handleAccept}
                    >
                        {t("Create")}
                    </CreateButton>
                </StyledDialogActions>
            </StyledBeautifulDialog>
        );
    }
}
